import React from "react";
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { format, parseISO } from "date-fns";

interface ChartDataPoint {
  date: string;
  new_ideas: number;
  new_outreach: number;
  new_milestones: number;
  new_deck_views: number;
}

interface MetricsGrowthChartProps {
  data: ChartDataPoint[];
}

export default function MetricsGrowthChart({ data }: MetricsGrowthChartProps) {
  const [activeTab, setActiveTab] = useState("ideas");

  const chartConfig = {
    ideas: {
      dataKey: "new_ideas",
      stroke: "#3b82f6",
      name: "New Ideas",
    },
    outreach: {
      dataKey: "new_outreach",
      stroke: "#f97316",
      name: "New Outreach",
    },
    milestones: {
      dataKey: "new_milestones",
      stroke: "#10b981",
      name: "New Milestones",
    },
    views: {
      dataKey: "new_deck_views",
      stroke: "#f59e0b",
      name: "Deck Views",
    },
  };

  const formattedData = data.map((d) => ({
    ...d,
    formattedDate: format(parseISO(d.date), "MMM d"),
  }));

  const renderChart = (config: typeof chartConfig.ideas) => (
    <ResponsiveContainer width="100%" height={300} className="print:h-48">
      <LineChart data={formattedData}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis
          dataKey="formattedDate"
          tick={{ fontSize: 12 }}
          className="print:text-[10px]"
        />
        <YAxis tick={{ fontSize: 12 }} className="print:text-[10px]" />
        <Tooltip
          contentStyle={{
            backgroundColor: "hsl(var(--background))",
            border: "1px solid hsl(var(--border))",
            borderRadius: "8px",
          }}
        />
        <Legend wrapperStyle={{ fontSize: "12px" }} />
        <Line
          type="monotone"
          dataKey={config.dataKey}
          stroke={config.stroke}
          strokeWidth={2}
          dot={{ r: 3 }}
          activeDot={{ r: 5 }}
          name={config.name}
        />
      </LineChart>
    </ResponsiveContainer>
  );

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="print:hidden">
      <TabsList className="grid w-full grid-cols-4 print:hidden">
        <TabsTrigger value="ideas">Ideas</TabsTrigger>
        <TabsTrigger value="outreach">Outreach</TabsTrigger>
        <TabsTrigger value="milestones">Milestones</TabsTrigger>
        <TabsTrigger value="views">Views</TabsTrigger>
      </TabsList>
      <TabsContent value="ideas" className="mt-4">
        {renderChart(chartConfig.ideas)}
      </TabsContent>
      <TabsContent value="outreach" className="mt-4">
        {renderChart(chartConfig.outreach)}
      </TabsContent>
      <TabsContent value="milestones" className="mt-4">
        {renderChart(chartConfig.milestones)}
      </TabsContent>
      <TabsContent value="views" className="mt-4">
        {renderChart(chartConfig.views)}
      </TabsContent>
    </Tabs>
  );
}